import { useEffect, useState } from 'react'

import useDebounceInput from 'customHooks/useDebounceInput'
import { getKongLocationAll } from 'features/Location/services/location'
import { callErrorMsg } from 'helpers/errorMsg'

const HUB_LOCATION_TYPE = 'HUB'
const HUB_FETCH_LIMIT = 20

type UseLocationHubsParamsType = {
  /** Skip the fetch when the consumer already supplies its own options. */
  enabled?: boolean
}

export type HubOptionType = {
  name: string
  value: string
}

/**
 * Fetches the HUB locations from the API (searchable by name) and normalizes
 * them into `{ name, value }` options.
 */
const toHubOption = (location: { location_id: number; location_name: string }): HubOptionType => ({
  name: location.location_name,
  value: String(location.location_id),
})

const useLocationHubs = ({ enabled = true }: UseLocationHubsParamsType = {}) => {
  const [options, setOptions] = useState<HubOptionType[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const fetchHubs = async (name: string) => {
    setIsLoading(true)
    try {
      const {
        data: {
          data: { location },
        },
      } = await getKongLocationAll({
        params: { name, type: HUB_LOCATION_TYPE, active: true, page: 0, limit: HUB_FETCH_LIMIT },
      })
      setOptions(location.map(toHubOption))
    } catch (error) {
      callErrorMsg(error)
    } finally {
      setIsLoading(false)
    }
  }

  const [searchValue, handleSearch] = useDebounceInput({
    onChange: (keyword) => {
      if (enabled) fetchHubs(keyword)
    },
    delay: 500,
  })

  // Prime the list with an initial page on mount.
  useEffect(() => {
    if (enabled) fetchHubs('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled])

  /**
   * Looks a hub up by id, for a stored value the paged list does not carry —
   * without it the field could only show the id it was given.
   */
  const resolveHubById = async (hubId: string): Promise<HubOptionType | null> => {
    try {
      const {
        data: {
          data: { location },
        },
      } = await getKongLocationAll({
        params: { id: hubId, type: HUB_LOCATION_TYPE, page: 0, limit: 1 },
      })

      return location.length ? toHubOption(location[0]) : null
    } catch (error) {
      callErrorMsg(error)
      return null
    }
  }

  return { options, isLoading, searchValue, handleSearch, resolveHubById }
}

export default useLocationHubs
